import React, { useRef, useState } from 'react';

import bell from '@assets/img/icons/bell.svg';
import { ExpandMoreButton } from '@components/ExpandMoreButton';
import { SidebarListItem } from '@components/SidebarListItem';
import { useLogout } from '@hooks/mutation/useLogout';
import { useNotifcationUnreadCount } from '@hooks/queries/useNotificationUnreadCount';
import { useUser } from '@hooks/queries/useUser';
import Collapse from '@material-ui/core/Collapse';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { AuthManager } from '@store/AuthManager';
import { makeMuiStyles, useCommonStyles, useIsMobile } from '@styles';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { observer } from 'mobx-react';
import { Link, useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    notification: 'お知らせ',
    bookingManagement: '案件管理',
    membership: 'D2D MEMBERS',
    accountSettings: 'アカウント設定',
    logOut: 'ログアウト',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexInline,
    alignSelf: 'stretch',
    alignItems: 'center',
  },
  bell: {
    display: 'block',
    width: 22,
    height: 22,
  },
  badge: {
    ...common.flexCenter,
    height: 14,
    width: 14,
    borderRadius: '50%',
    backgroundColor: theme.palette.warning.dark,
    color: theme.palette.warning.contrastText,
    position: 'absolute',
    top: -5,
    right: -5,
    fontSize: 10,
    fontFamily: 'Arial, sans',
    paddingBottom: 2,
  },
  split: {
    height: 20,
    width: 1,
    backgroundColor: theme.palette.grey['700'],
    margin: '0 20px',
  },
  username: {
    ...common.normalText,
    ...common.flexCenter,
    ...common.baseButton,
    color: theme.palette.common.white,
    userSelect: 'none',
  },
  menuPaper: {
    borderRadius: 0,
    zIndex: 10000,
  },
  menuList: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  menuItemRoot: {
    ...common.middleText,
    paddingTop: '1rem',
    paddingBottom: '1rem',
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    color: theme.palette.text.primary,
    '& + *': {
      borderTopStyle: 'solid',
      borderTopWidth: 1,
      borderTopColor: theme.palette.grey['100'],
    },
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: 'initial',
    },
  },
  menuDivider: {
    height: 6,
    backgroundColor: theme.palette.grey['100'],
  },
  nested: {
    paddingLeft: theme.spacing(2),
  },
}));

export const UserMenu: React.FC = observer(() => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const history = useHistory();
  const { data } = useUser(undefined, {
    enabled: AuthManager.shared.isLoggedIn,
  });
  const logout = useLogout();
  const unreadCount = useNotifcationUnreadCount();

  const isMobile = useIsMobile();

  const anchorRef = useRef<any>();
  const [menuAnchor, setMenuAnchor] = useState<any>(null);

  const [openUserMenu, setOpenUserMenu] = useState(false);

  const handleClose = () => {
    setMenuAnchor(null);
  };

  const renderMenu = () => {
    return (
      <Menu
        anchorEl={menuAnchor}
        keepMounted
        open={Boolean(menuAnchor)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 40,
        }}
        getContentAnchorEl={undefined}
        elevation={0}
        classes={{
          paper: classes.menuPaper,
          list: classes.menuList,
        }}
        autoFocus={false}
      >
        <MenuItem
          onClick={() => history.push(url('/settings/Membership/CompanyInfo'))}
          classes={{ root: classes.menuItemRoot }}
        >
          {strings.membership}
        </MenuItem>
        <MenuItem
          onClick={() => history.push(url('/settings/AccountManager/Profile'))}
          classes={{ root: classes.menuItemRoot }}
        >
          {strings.accountSettings}
        </MenuItem>
        <div
          className={classes.menuDivider}
          onClick={() =>
            process.env.NODE_ENV === 'development' && history.push(url('/dev/Accounts'))
          }
        />
        <MenuItem
          onClick={async () => {
            await logout.mutateAsync();
            history.push(url('/auth/Login'));
          }}
          classes={{ root: classes.menuItemRoot }}
        >
          {strings.logOut}
        </MenuItem>
      </Menu>
    );
  };

  const renderContentsUpMd = (username: string) => {
    return (
      <div ref={anchorRef} className={classes.container}>
        <Link
          to={url('/utilities/NotificationCenter/NotificationCenter')}
          style={{ position: 'relative', cursor: 'pointer' }}
        >
          <img src={bell} alt="Notifications" className={classes.bell} />
          {unreadCount!.data! > 0 && <div className={classes.badge}>{unreadCount.data}</div>}
        </Link>
        <div className={classes.split} />

        <div
          className={classes.username}
          onClick={() => setMenuAnchor(menuAnchor ? null : anchorRef.current)}
        >
          <div data-testid="username">{username}</div>
          {AuthManager.shared.impersonatedUser ? (
            <Link to={url('/dev/Impersonate')}>&nbsp;(IMP)</Link>
          ) : null}
          <ExpandMoreButton expanded={Boolean(menuAnchor)} />
        </div>
        {renderMenu()}
      </div>
    );
  };

  const renderContentsDownMd = (username: string) => {
    return (
      <List>
        <SidebarListItem to={url('/utilities/NotificationCenter/NotificationCenter')}>
          <ListItemText primary={strings.notification} />
        </SidebarListItem>
        <ListItem button onClick={() => setOpenUserMenu((state) => !state)}>
          <ListItemText primary={username} />
          {openUserMenu ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={openUserMenu} timeout="auto" unmountOnExit>
          <List disablePadding className={classes.nested}>
            <SidebarListItem to={url('/settings/Membership/CompanyInfo')}>
              <ListItemText primary={strings.membership} />
            </SidebarListItem>
            <SidebarListItem to={url('/settings/AccountManager/Profile')}>
              <ListItemText primary={strings.accountSettings} />
            </SidebarListItem>
            <ListItem
              button
              onClick={async () => {
                await logout.mutateAsync();
                AuthManager.shared.clear();
                history.push(url('/auth/Login'));
              }}
            >
              <ListItemText primary={strings.logOut} />
            </ListItem>
          </List>
        </Collapse>
      </List>
    );
  };

  if (data == null || !unreadCount.isSuccess) {
    return null;
  }

  const username = `${data.last_name} ${data.first_name}`;

  return isMobile ? renderContentsDownMd(username) : renderContentsUpMd(username);
});
